// Client wrapper over the `lookup-barcode` edge function.
//
// Checks the local brand_nutrition cache first (useBrandNutrition's
// rows) so a barcode we've already seen resolves without a round
// trip. On a cache miss, invokes the edge function which queries
// Open Food Facts and returns mapped nutrition data.
//
// Returns a single shape regardless of source so the caller doesn't
// have to fork on cache-vs-network:
//
//   { found, barcode, brand, productName, categoryHints, nutrition,
//     source, sourceId, offUrl, cached }
//
// `cached: true` means we resolved from the realtime cache; the
// caller can skip the upsert step (the row already exists).

import { supabase } from "./supabase";

// Photo-capture fallback for devices that can't do live BarcodeDetector
// (iOS PWA standalones, Firefox mobile, older browsers). Sends a photo
// to the decode-barcode-image edge function, which uses Claude vision
// to read the human-readable digits printed below every UPC/EAN
// barcode. Returns { found: true, barcode } or { found: false, reason }.
//
// `image` is a base64 string WITHOUT the data: prefix. `mediaType`
// is one of image/jpeg | image/png | image/webp.
export async function decodeBarcodeFromImage(image, mediaType = "image/jpeg") {
  if (!image) return { found: false, reason: "no_image" };
  const { data, error } = await supabase.functions.invoke("decode-barcode-image", {
    body: { image, mediaType },
  });
  if (error) {
    let detail = "";
    let status = null;
    const ctx = error.context;
    if (ctx) {
      status = ctx.status ?? null;
      if (typeof ctx.text === "function") {
        try { detail = await ctx.text(); } catch { /* noop */ }
      }
    }
    console.error("[decode-barcode-image] edge fn failed:", { message: error.message, status, detail });
    const reason = status === 404 ? "edge_fn_not_deployed" : "decode_failed";
    return { found: false, reason, status, detail: detail || error.message };
  }
  return data || { found: false, reason: "empty_response" };
}

// localStorage cache for OFF lookup results. Each `lookup-barcode`
// edge-fn call sends a small request body (just the barcode) but
// the response carries the full OFF product payload (~5-15 KB).
// Re-scanning a known UPC was re-firing the edge fn every time.
// 7-day TTL — OFF data is stable; product info rarely changes.
// Negative results cached 1h so a temporarily-down OFF doesn't
// permanently lock a UPC into not-found.
const OFF_CACHE_KEY     = "offLookupCache.v1";
const OFF_TTL_FOUND_MS  = 7 * 24 * 60 * 60 * 1000;
const OFF_TTL_NOTFOUND_MS = 60 * 60 * 1000;

function readOffCache(barcode) {
  try {
    if (typeof localStorage === "undefined") return null;
    const raw = localStorage.getItem(OFF_CACHE_KEY);
    if (!raw) return null;
    const all = JSON.parse(raw);
    const entry = all?.[barcode];
    if (!entry || typeof entry.fetchedAt !== "number") return null;
    const ttl = entry.found ? OFF_TTL_FOUND_MS : OFF_TTL_NOTFOUND_MS;
    if (Date.now() - entry.fetchedAt > ttl) return null;
    return entry.data;
  } catch { return null; }
}

function writeOffCache(barcode, data) {
  try {
    if (typeof localStorage === "undefined") return;
    const raw = localStorage.getItem(OFF_CACHE_KEY);
    const all = raw ? JSON.parse(raw) : {};
    all[barcode] = { fetchedAt: Date.now(), found: !!data?.found, data };
    // Bound the cache size — drop oldest entries past 200 to keep
    // localStorage from growing unbounded for users who scan a lot.
    const keys = Object.keys(all);
    if (keys.length > 200) {
      const sorted = keys
        .map(k => [k, all[k].fetchedAt])
        .sort((a, b) => a[1] - b[1]);
      const drop = sorted.slice(0, sorted.length - 200).map(s => s[0]);
      for (const k of drop) delete all[k];
    }
    localStorage.setItem(OFF_CACHE_KEY, JSON.stringify(all));
  } catch { /* quota / private mode */ }
}

export async function lookupBarcode(barcode, { brandNutritionRows = [], ensureByBarcode = null } = {}) {
  const normalized = String(barcode || "").trim();
  if (!/^\d{8,14}$/.test(normalized)) {
    return { found: false, barcode: normalized, reason: "invalid_barcode" };
  }

  // localStorage cache check — re-scans of the same UPC return
  // instantly without re-firing the edge function. Massive egress
  // win for users who scan the same products repeatedly (most users).
  const offCached = readOffCache(normalized);
  if (offCached) {
    return { ...offCached, cached: true };
  }

  // Cache check — two paths supported for the cache lookup:
  //   1. ensureByBarcode (preferred): from useBrandNutrition's lazy-
  //      load API. Single-row Supabase query, returns the row or
  //      null. This is the ONLY path that scales — the brand_nutrition
  //      table grows with every user globally; we never want to pull
  //      it all client-side.
  //   2. brandNutritionRows (legacy fallback): pre-fetched array
  //      passed in. Kept for back-compat with callers that haven't
  //      migrated. New call sites should prefer ensureByBarcode.
  let cached = (brandNutritionRows || []).find(r => r.barcode === normalized) || null;
  if (!cached && typeof ensureByBarcode === "function") {
    cached = await ensureByBarcode(normalized);
  }

  // Always hit OFF for the text fields. The brand_nutrition cache
  // doesn't carry productName / genericName / labels_tags / etc., so
  // returning from cache alone leaves claim extraction (SCOOPS,
  // ORIGINAL, …) with null input. When OFF comes back, we use its
  // text and fall back to cached nutrition if OFF is missing it.
  const { data, error } = await supabase.functions.invoke("lookup-barcode", {
    body: { barcode: normalized },
  });

  console.log("[lookup-barcode] edge-fn result", {
    barcode: normalized,
    hasError: !!error,
    errorMessage: error?.message,
    errorStatus: error?.context?.status ?? null,
    dataFound: data?.found,
    dataReason: data?.reason,
    dataProductName: data?.productName,
    willFallBackToCache: !!(error || !data?.found) && !!cached,
  });

  if (!error && data && data.found) {
    // Merge: OFF provides fresh text, cache can top up nutrition if
    // OFF didn't have it (older OFF entries without nutriments).
    const result = {
      ...data,
      nutrition: data.nutrition || cached?.nutrition || {},
      cached: false,
    };
    // Persist the merged result to localStorage so the next scan of
    // this UPC skips the edge fn entirely (within TTL).
    writeOffCache(normalized, result);
    return result;
  }
  // Cache "not found" too so we don't keep re-asking OFF for UPCs
  // it doesn't know. Shorter TTL (1h) so a temporary OFF outage
  // doesn't permanently lock a barcode out.
  if (!error && data && !data.found) {
    writeOffCache(normalized, { ...data, found: false });
  }

  // OFF failed. Fall back to the cached row if we have one — better
  // to surface the nutrition we know than a hard error.
  if (cached) {
    return {
      found:         true,
      barcode:       normalized,
      brand:         cached.displayBrand || null,
      productName:   null,
      categoryHints: [],
      nutrition:     cached.nutrition || {},
      source:        cached.source || "cache",
      sourceId:      cached.sourceId || normalized,
      offUrl:        `https://world.openfoodfacts.org/product/${normalized}`,
      cached:        true,
      canonicalId:   cached.canonicalId || null,
    };
  }

  // OFF failed and no cache — surface a structured error for the UI.
  if (error) {
    let detail = "";
    let status = null;
    const ctx = error.context;
    if (ctx) {
      status = ctx.status ?? null;
      if (typeof ctx.text === "function") {
        try { detail = await ctx.text(); } catch { /* noop */ }
      }
    }
    console.error("[lookup-barcode] edge fn failed:", { message: error.message, status, detail });
    const reason = status === 404 ? "edge_fn_not_deployed" : "fetch_failed";
    return { found: false, barcode: normalized, reason, status, detail: detail || error.message };
  }
  return { ...(data || { found: false, barcode: normalized }), cached: false };
}
